import {
  FlatfileProvider,
  useFlatfile,
  Workbook,
  Space,
  useEvent,
} from "@flatfile/react";
import api from "@flatfile/api";
import { Button } from "@pushpress/shared-components";
import { isProd } from "../../../env";
import { workbookConfig } from "../FlatFileConfigs/workbookConfig";
import {
  FLATFILE_PUBLISHABLE_KEY_DEV,
  FLATFILE_PUBLISHABLE_KEY_PROD,
} from "../constants";
import { spaceConfig } from "../FlatFileConfigs/spaceConfig";
import {
  useGetFlatFileAccessToken,
  useMutateMembers,
} from "../api/migrationAssistantApi.hooks";
import {
  flatFileRecordModifiers,
  transformMemberDataForPayload,
  validSubaccounts,
} from "../helpers";
import LoadingState from "./shared/LoadingState";
import { useQueryClient } from "@tanstack/react-query";
import "./CardComponent.module.scss";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { fetchSheetRecords } from "../api/migrationAssistantApi";

type FlatFileUIType = {
  buttonText: string;
};

export function FlatFileUI({ buttonText }: FlatFileUIType) {
  const { data, isFetching } = useGetFlatFileAccessToken();

  const PUBLISHABLE_KEY = isProd()
    ? FLATFILE_PUBLISHABLE_KEY_PROD
    : FLATFILE_PUBLISHABLE_KEY_DEV;

  /**
   * AccessToken and spaceId are storred on the backend after
   * the user successfully uploads data. They are needed to open
   * an existing spreadsheet. PublishableKey is storred on the
   * frontend and is needed to create a new workbook.
   */
  const flatFileProviderSetup = {
    ...(data?.accessToken
      ? { accessToken: data.accessToken }
      : { publishableKey: PUBLISHABLE_KEY }),
  };

  if (isFetching) {
    return <LoadingState width={325} height={38} />;
  }

  return (
    <FlatfileProvider {...flatFileProviderSetup}>
      <WorkbookConfig spaceId={data?.spaceId} buttonText={buttonText} />
    </FlatfileProvider>
  );
}

type WorkbookConfigType = {
  buttonText: string;
  spaceId?: string;
};

const WorkbookConfig = ({ buttonText, spaceId }: WorkbookConfigType) => {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { open, openPortal, closePortal } = useFlatfile();
  const updateMembers = useMutateMembers();

  useEvent("job:ready", { job: "workbook:submitActionFg" }, async (event) => {
    const {
      context: { jobId, workbookId, spaceId },
    } = event;

    try {
      const { data: sheets } = await api.sheets.list({ workbookId });
      const records = await fetchSheetRecords(sheets);

      if (!records) {
        await api.jobs.fail(jobId, {
          outcome: {
            message: "There was an error fetching records",
          },
        });
        trackEvent("Migration Assistant Error", {
          error: "ma_flatfile_fetch_records_error",
          space_id: spaceId,
        });
        return;
      }

      const payloadMembersData = transformMemberDataForPayload(records);

      if (!validSubaccounts(payloadMembersData)) {
        await api.jobs.fail(jobId, {
          outcome: {
            message:
              "There was a problem assigning subaccounts, check your data",
          },
        });
        return;
      }

      updateMembers.mutate(
        {
          spaceId,
          data: payloadMembersData,
        },
        {
          onSuccess: async () => {
            await api.jobs.complete(jobId, {
              outcome: {
                message: "This is now complete.",
              },
            });
            queryClient.invalidateQueries({
              queryKey: ["fetchMigratingMembers"],
            });
            closePortal();
          },
          onError: async (error) => {
            await api.jobs.fail(jobId, {
              outcome: {
                message: "There was an error uploading members",
              },
            });
            trackEvent("Migration Assistant Error", {
              error: "ma_api_upload_members_error",
              space_id: spaceId,
              api_error: JSON.stringify(error),
            });
          },
        },
      );
    } catch (error: unknown) {
      await api.jobs.fail(jobId, {
        outcome: {
          message: JSON.stringify(error),
        },
      });
      trackEvent("Migration Assistant Error", {
        error: "ma_api_upload_members_catch_error",
        space_id: spaceId,
        api_error: JSON.stringify(error),
      });
    }
  });

  return (
    <>
      <Button
        buttonType="secondary"
        size="large"
        fullWidth
        onClick={() => {
          open ? closePortal() : openPortal();
        }}
        text={open ? "Close" : buttonText}
      />
      <Space config={spaceConfig} id={spaceId}>
        <Workbook
          config={workbookConfig}
          onRecordHooks={[
            ["membersInfo", (record) => flatFileRecordModifiers(record)],
          ]}
        />
      </Space>
    </>
  );
};
